/**
 * Upload Service
 * Handles file uploads via ActiveStorage Direct Upload
 */

import { api } from '@/lib/axios';
import { getServerBaseURL } from '@/lib/axios';
import SparkMD5 from 'spark-md5';

interface DirectUploadResponse {
  signed_id: string;
  key: string;
  filename: string;
  content_type: string;
  byte_size: number;
  checksum: string;
  created_at: string;
  service_url: string;
  direct_upload: {
    url: string;
    headers: Record<string, string>;
  };
}

export const uploadService = {
  /**
   * Upload a file using ActiveStorage direct upload
   *
   * @param file - File to upload
   * @param onProgress - Optional progress callback
   * @returns Signed blob ID
   */
  async uploadFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    // Step 1: Request presigned URL from backend
    const directUploadData = await this.requestDirectUpload(file);

    // Step 2: Upload file to storage (S3/local) with progress tracking
    await this.uploadToStorage(
      file,
      directUploadData.direct_upload.url,
      directUploadData.direct_upload.headers,
      onProgress
    );

    // Step 3: Return signed blob ID for attaching to models
    return directUploadData.signed_id;
  },

  /**
   * Request a presigned URL for direct upload
   */
  async requestDirectUpload(file: File): Promise<DirectUploadResponse> {
    const checksum = await this.calculateChecksum(file);
    const serverBaseURL = getServerBaseURL();

    // ActiveStorage direct upload endpoint is NOT under /api
    const response = await fetch(`${serverBaseURL}/rails/active_storage/direct_uploads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        blob: {
          filename: file.name,
          content_type: file.type,
          byte_size: file.size,
          checksum: checksum,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to request direct upload: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Upload file to storage using presigned URL
   */
  async uploadToStorage(
    file: File,
    url: string,
    headers: Record<string, string>,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', url);

      // IMPORTANT: Only set headers provided by ActiveStorage
      // Do NOT add Content-Disposition or other headers as they will break S3 signature
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(file);
    });
  },

  /**
   * Calculate MD5 checksum for file
   * ActiveStorage requires MD5 in base64 format
   */
  async calculateChecksum(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const spark = new SparkMD5.ArrayBuffer();
    spark.append(buffer);
    const md5Hash = spark.end();

    // Convert hex MD5 to base64 as expected by ActiveStorage
    const hexBytes = md5Hash.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16));
    const md5Bytes = new Uint8Array(hexBytes);
    return btoa(String.fromCharCode(...md5Bytes));
  },

  /**
   * Delete a photo from an item
   */
  async deletePhoto(itemId: string, photoId: number): Promise<void> {
    await api.delete(`/v1/admin/items/${itemId}/photos/${photoId}`);
  },
};
